import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import * as Sentry from '@sentry/react'
import { Circle } from 'lucide-react'
import { toast, Toaster } from 'sonner'
import { useShallow } from 'zustand/react/shallow'

import Header from '../../components/Header'
import { Button } from '@harvey-frontend/components/ui/button'
import { Separator } from '@harvey-frontend/components/ui/separator'
import Toast from '@harvey-frontend/components/ui/toast'
import Error from 'components/common/error/error'
import { ScrollArea } from 'components/ui/scroll-area'

import {
  MatchedReference,
  StoredReviewItem,
  StoredReviewResponse,
} from '../../../../schemas/playbook'
import { useInitialReference } from '../../hooks/useInitialReference'
import { usePlaybookRuleSuggestions } from '../../hooks/usePlaybookRuleSuggestions'
import useSelectedText from '../../hooks/useSelectedText'
import useAppStore from '../../stores/store'
import { ensureError } from '../../utils/error-utils'
import { RuleDetails } from '../PlaybookReviewScreen/RuleDetails'
import { ReferenceItem } from './ReferenceItem'

type RuleAttributes = {
  ruleData: StoredReviewItem
  referencesText: string
  color: 'green' | 'gold' | 'red'
  headerTitle: string
}

const getRuleAttributes = (
  reviewResult: StoredReviewResponse | null,
  index: number
): RuleAttributes | null => {
  if (!reviewResult) return null
  switch (reviewResult.review[index].status) {
    case 'acceptable':
      return {
        ruleData: reviewResult.review[index],
        referencesText: 'Related text',
        color: 'green',
        headerTitle: 'Acceptable',
      }
    case 'needs_review':
      return {
        ruleData: reviewResult.review[index],
        referencesText: 'Text to review',
        color: 'gold',
        headerTitle: 'Needs review',
      }
    case 'unacceptable':
      return {
        ruleData: reviewResult.review[index],
        referencesText: 'Text to review',
        color: 'red',
        headerTitle: 'Not acceptable',
      }
  }
}

const getPreviousAndNextUnresolvedIndices = (
  reviewResult: StoredReviewResponse,
  index: number
) => {
  let previousIndex = index - 1
  let nextIndex = index + 1
  while (
    previousIndex >= 0 &&
    previousIndex !== index &&
    reviewResult.review[previousIndex].resolved
  ) {
    previousIndex--
  }
  while (
    nextIndex < reviewResult.review.length &&
    nextIndex !== index &&
    reviewResult.review[nextIndex].resolved
  ) {
    nextIndex++
  }
  return { previousIndex, nextIndex }
}

export const PlaybookRuleScreen = () => {
  const { playbook, reviewResult, markRuleResolved, setHighlightedText } =
    useAppStore(
      useShallow((s) => ({
        playbook: s.playbook,
        reviewResult: s.reviewResult,
        markRuleResolved: s.markRuleResolved,
        setHighlightedText: s.setHighlightedText,
      }))
    )
  useSelectedText(setHighlightedText)
  const navigate = useNavigate()
  const { index: indexString } = useParams() as { index: string }
  const index = parseInt(indexString)
  const rule = playbook?.rules.find(
    (rule) => rule.name === reviewResult?.review[index].ruleName
  )
  const { mutateAsync, reset, isPending } = usePlaybookRuleSuggestions()
  const ruleAttributes = getRuleAttributes(reviewResult, index)

  useInitialReference(ruleAttributes?.ruleData)

  const [isAnimating, setIsAnimating] = useState(false)

  // Effect to generate redline suggestions for the rule if it has references.
  useEffect(() => {
    if (!rule || !ruleAttributes?.ruleData.references) {
      return
    }

    const abortControllers: AbortController[] = []

    ruleAttributes.ruleData.references.forEach(async (reference) => {
      if (
        reference.suggestions.length > 0 ||
        ruleAttributes.ruleData.status === 'acceptable'
      ) {
        return
      }

      const abortController = new AbortController()
      abortControllers.push(abortController)

      reset()
      const params = {
        ruleName: rule.name,
        clauseText: reference.range?.text || reference.text,
        standardPosition: rule.standardPosition || '',
        acceptableDeviations: rule.acceptableDeviations || [],
        referenceId: reference.id,
        abortController,
      }

      try {
        await mutateAsync(params)
      } catch (e) {
        const error = ensureError(e)
        Sentry.captureException(error)
        console.error(error)
      }
    })

    return () => {
      abortControllers.forEach((controller) => controller.abort())
    }
  }, [
    rule,
    ruleAttributes?.ruleData.references,
    ruleAttributes?.ruleData.status,
    mutateAsync,
    reset,
  ])

  if (!playbook || !reviewResult || !rule || !ruleAttributes) {
    return (
      <Error
        primaryCTA={{
          text: 'Go Back',
          onClick: () => navigate('/app-selection'),
        }}
        description="You arrived at this page in an invalid state. Please return to the playbook review screen and try again."
      />
    )
  }

  const { ruleData, referencesText, color, headerTitle } = ruleAttributes

  const { previousIndex, nextIndex } = getPreviousAndNextUnresolvedIndices(
    reviewResult,
    index
  )
  const previousNav = `/playbook-rule/${previousIndex}`
  const nextNav = `/playbook-rule/${nextIndex}`
  const itemsInSection = reviewResult.review.filter(
    (item) => item.status === ruleAttributes?.ruleData.status && !item.resolved
  )
  const indexInSection = itemsInSection.findIndex(
    (item) => item.ruleName === ruleAttributes?.ruleData.ruleName
  )

  const handleMarkResolved = () => {
    setIsAnimating(true)
    toast.custom(
      (t) => (
        <Toast
          message="Rule resolved"
          toastId={t}
          variant="success"
          width="full"
        />
      ),
      {
        position: 'bottom-center',
        duration: 1000,
        className: 'bottom-16',
      }
    )
    markRuleResolved(ruleData.ruleName)

    let nextNav = `/playbook-rule/${nextIndex}`
    if (nextIndex >= reviewResult.review.length) {
      if (previousIndex < 0) {
        nextNav = '/playbook-review'
      } else {
        nextNav = `/playbook-rule/${previousIndex}`
      }
    }

    setTimeout(() => {
      navigate(nextNav, {
        state: { useTransition: true },
      })
    }, 1000)
  }

  const nextLink = (() => {
    if (nextIndex >= reviewResult.review.length) {
      return (
        <Link to="/playbook-review">
          <Button disabled={isAnimating}>Finish</Button>
        </Link>
      )
    }
    return (
      <Link to={nextNav} viewTransition>
        <Button disabled={isAnimating}>Next</Button>
      </Link>
    )
  })()

  const pendingContent = isPending ? (
    <span className="text-xs text-secondary">Loading suggestions…</span>
  ) : null

  return (
    <ScrollArea maxHeight="h-screen" isFullHeight>
      <Toaster
        className="flex w-full justify-center p-8"
        position="bottom-center"
      />
      <div className="flex h-full flex-col">
        <Header
          title={headerTitle}
          icon={<Circle className="h-2.5 w-2.5" fill={color} stroke={color} />}
          to="/playbook-review"
          state={{ expandedSection: ruleData.status }}
        />
        <div
          className={`flex-1 overflow-auto p-4 transition-opacity duration-200 ease-in-out ${
            isAnimating ? 'opacity-60' : 'opacity-100'
          }`}
        >
          <div className="mb-4 flex flex-col gap-1">
            <span className="text-sm font-medium">{ruleData.ruleName}</span>
            <p className="text-xs text-secondary">
              <span className="font-medium">Harvey&apos;s summary · </span>
              {ruleData.explanation}
            </p>
          </div>
          <RuleDetails
            rule={
              playbook.rules.find((rule) => rule.name === ruleData.ruleName)!
            }
            headerContent={
              <div className="flex w-full items-center justify-between">
                <span className="text-xs font-medium text-primary">
                  View playbook rule
                </span>
              </div>
            }
          />
          {ruleData.references && ruleData.references.length > 0 && (
            <>
              <Separator className="mt-4" />
              <div className="flex flex-col gap-2 pt-2">
                <div className="flex w-full items-center justify-between pb-2">
                  <span className="text-sm font-medium">{referencesText}</span>
                  {pendingContent}
                </div>
                {ruleData.references?.map((ref: MatchedReference, index) => (
                  <ReferenceItem
                    key={index}
                    reference={ref}
                    rule={rule}
                    isHighlighted={ref.id === ruleData.highlightedReferenceId}
                    suggestionsPending={isPending}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        <footer className="flex items-center justify-between border-t p-4">
          <span className="text-xs text-muted [@media(max-width:290px)]:hidden">
            {indexInSection + 1} of {itemsInSection.length}
          </span>
          <div className="flex gap-2">
            <Link to={previousNav} viewTransition>
              <Button
                variant="outline"
                disabled={previousIndex < 0 || isAnimating}
              >
                Back
              </Button>
            </Link>
            <Button
              variant="outline"
              onClick={handleMarkResolved}
              disabled={isAnimating}
            >
              Mark resolved
            </Button>
            {nextLink}
          </div>
        </footer>
      </div>
    </ScrollArea>
  )
}
